import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ROUTER_CATALOG_DH_L3 } from 'app/AppRouter';
import { OperationsIcon } from 'assets/icons';
import { FirstHelpPopin } from 'components/Help/FirstHelpPopin';
import { getEasyBasketEnabled } from 'domains/appContext/AppContext.store';
import {
  ActiveTabParam,
  PARAM_LABOR_TIME,
  PARAM_OPERATIONS,
  PARAM_SPARE_PARTS,
  PreferredTabParam,
} from 'domains/catalog/Catalog.types';
import { usePreferredTabHook } from 'pages/CatalogPage/common/SubcategorySection/SubcategorySectionHeader.hook';
import { theme } from 'styles';
import { Container, Flex, Icon, LinkTabs, MarginBox, sentenceCase, Text } from 'UI';
import { getCondArrayItem } from 'utils';

export type TabState = 'spare_parts' | 'labor_time' | 'repair_methods' | 'operations';

export const SubcategorySectionHeader = ({
  tabState,
  setTabState,
  disabledTabs,
  label,
}: {
  tabState: TabState;
  setTabState: React.Dispatch<React.SetStateAction<TabState>>;
  disabledTabs: TabState[];
  label: string | undefined;
}) => {
  const { t } = useTranslation();
  const isEasyBasketFeatureEnabled = useSelector(getEasyBasketEnabled);
  const isEasyBasketTabEnabled = !disabledTabs.includes(PARAM_OPERATIONS);

  usePreferredTabHook(tabState, setTabState, disabledTabs, getParamState);

  function getLabel(tab: TabState, isDisabled = false) {
    switch (tab) {
      case PARAM_SPARE_PARTS:
        return t('catalog.parts.category.car_parts.parts', 'Spare parts');
      case PARAM_LABOR_TIME:
        return t('catalog.parts.category.car_parts.labor_time', 'Labor time');
      case PARAM_OPERATIONS:
        return (
          <Flex size={0} gap={15} align={'center'}>
            {t('catalog.operations.title', 'Operations')}
            <Icon IconComponent={OperationsIcon} size={24} disabled={isDisabled} />
          </Flex>
        );
      default:
        return null;
    }
  }

  function getParamState(tab: TabState) {
    const urlParams = new URLSearchParams(location.search);
    urlParams.set(ActiveTabParam, tab);
    urlParams.set(PreferredTabParam, tab);
    return urlParams;
  }

  const tabItems = [
    {
      label: getLabel(PARAM_SPARE_PARTS),
      link: '',
      uparam: getParamState(PARAM_SPARE_PARTS),
      key: 0,
      isDisabled: disabledTabs.includes(PARAM_SPARE_PARTS),
      dataCy: 'spare-parts-button',
    },
    {
      label: getLabel(PARAM_LABOR_TIME),
      link: '',
      uparam: getParamState(PARAM_LABOR_TIME),
      key: 1,
      isDisabled: disabledTabs.includes(PARAM_LABOR_TIME),
      dataCy: 'labor-time-button',
    },
    ...getCondArrayItem(
      isEasyBasketFeatureEnabled &&
        isEasyBasketTabEnabled && {
          label: getLabel(PARAM_OPERATIONS, !isEasyBasketTabEnabled),
          link: '',
          uparam: getParamState(PARAM_OPERATIONS),
          key: 2,
          isDisabled: !isEasyBasketTabEnabled,
          dataCy: 'operations-button',
        },
    ),
  ];

  return (
    <Flex direction={'column'}>
      <Flex minHeight={44} background={theme.color.grey90}>
        <FirstHelpPopin
          streamId={ROUTER_CATALOG_DH_L3}
          popinId={`${ROUTER_CATALOG_DH_L3}_plate_detail`}
          placement={'bottomLeft'}
        >
          <Flex>
            <LinkTabs items={tabItems} variant={'greyWhite'} gap={0} />
          </Flex>
        </FirstHelpPopin>
      </Flex>
      {label && (
        <Container size={'xxl'}>
          <MarginBox mt={6} />
          <Text type={'h1_banner_light'}>{sentenceCase(label ?? '')}</Text>
        </Container>
      )}
    </Flex>
  );
};
